/**
 * Layer Type Guards
 *
 * Type predicates for determining layer rendering mode (polygon vs line).
 */

import type { LayerType } from "../themes/types";

/**
 * Determine if a layer type uses polygon (fill) or line rendering.
 */
export function isPolygonLayerType(layerType: string): boolean {
  const polygonTypes: LayerType[] = [
    "boundary",
    "water",
    "vegetation",
    "landscape",
    "parking",
    "building",
    "building_primary",
    "building_secondary",
    "building_entrance",
    "zone",
    "custom_polygon",
  ];
  return polygonTypes.includes(layerType as LayerType);
}

/**
 * Building layer types that benefit from corner-rounding (border-radius style)
 * instead of Chaikin smoothing.
 */
export function isBuildingLayerType(layerType: string): boolean {
  const buildingTypes: LayerType[] = [
    "building",
    "building_primary",
    "building_secondary",
    "building_entrance",
  ];
  return buildingTypes.includes(layerType as LayerType);
}

/**
 * Line layer types that should render as lines.
 */
export function isLineLayerType(layerType: string): boolean {
  const lineTypes: LayerType[] = [
    "path_primary",
    "path_secondary",
    "path_tertiary",
    "path_wide",
    "bridge",
    "tunnel",
    "barrier",
    "rail",
    "bus",
    "waterway",
    "custom_path",
  ];
  return lineTypes.includes(layerType as LayerType);
}

/**
 * Layer types that accept BOTH LineString and Polygon geometry.
 * Renderers branch by feature.geometry.type to pick line vs fill output.
 * `path_wide`: line = paved walkway segment; polygon = plaza/plaza-fill (cross-plaza routing).
 */
export const DUAL_SHAPE_LAYER_TYPES: LayerType[] = ["path_wide"];

export function isDualShapeLayerType(layerType: string): boolean {
  return DUAL_SHAPE_LAYER_TYPES.includes(layerType as LayerType);
}
